/**
 * The header peon's mood (docs/peons.md "Moods", flag `peonMoodsUI`): what he does on top of
 * blinking and mumbling, decided from the board's state alone. Pure and dependency-free, read by the
 * web (src/components/PixelPeon.tsx) and the app (client/components/PixelPeon.tsx).
 *
 * - "rub": a gold rush is on, so he rubs his hands every 15 to 30 seconds.
 * - "sit": the board has not changed on this device for an hour, so he sits down and stays sat
 *   (still blinking) until something changes, then gets up.
 * - "yawn": the board is empty (no open task), so he yawns every 20 to 40 seconds.
 * - null: none of these; he is as he always was.
 *
 * A gold rush wins (gold on offer wakes anyone up), then sitting (an hour of nothing beats an empty
 * board: he has given up waiting), then the yawn.
 */
export type PeonMoodName = "yawn" | "rub" | "sit";

/** An hour with no change on the board and he sits down. */
export const SIT_AFTER_MS = 60 * 60 * 1000;

export interface PeonMoodInput {
  /** Open tasks on the board, null while it has not loaded yet (no yawn on an unknown board). */
  openCount: number | null;
  /** Whether a gold rush is on now (shared/flash.ts isFlashActive). */
  flashOn: boolean;
  /** How long the board has gone without a change on this device; or give `lastChangeAt` instead. */
  quietMs?: number | null;
  /** When the board last changed on this device (ms since epoch); used when `quietMs` is not given. */
  lastChangeAt?: number | null;
  now: number;
}

export function peonMood({ openCount, flashOn, quietMs, lastChangeAt, now }: PeonMoodInput): PeonMoodName | null {
  if (flashOn) return "rub";
  const quiet = typeof quietMs === "number" ? quietMs : typeof lastChangeAt === "number" ? now - lastChangeAt : 0;
  if (Number.isFinite(quiet) && quiet >= SIT_AFTER_MS) return "sit";
  if (openCount === 0) return "yawn";
  return null;
}

/** How often a looping mood plays, in ms: [shortest, longest] between one play and the next. */
export const MOOD_EVERY_MS: Record<"yawn" | "rub", readonly [number, number]> = {
  yawn: [20_000, 40_000],
  rub: [15_000, 30_000],
};

/** The wait before the next play of a looping mood, `rand` in [0, 1). */
export function nextMoodDelayMs(mood: "yawn" | "rub", rand: () => number): number {
  const [min, max] = MOOD_EVERY_MS[mood];
  return min + rand() * (max - min);
}

/**
 * One play of a looping mood as hard frames: each step is a cell index into the mood's frames
 * (shared order with public/ui/peon-moods-v1.json) and how long it holds. Frame 0 is the mood's
 * still, the one reduced motion shows.
 */
export const MOOD_FRAMES: Record<"yawn" | "rub", readonly { frame: number; ms: number }[]> = {
  // Drowsy, the O opens, the jaw drops and holds, back through the O to drowsy.
  yawn: [
    { frame: 0, ms: 400 },
    { frame: 1, ms: 220 },
    { frame: 2, ms: 1100 },
    { frame: 1, ms: 220 },
    { frame: 0, ms: 600 },
  ],
  // Hands together, then rubbed back and forth four times.
  rub: [
    { frame: 0, ms: 160 },
    { frame: 1, ms: 160 },
    { frame: 0, ms: 160 },
    { frame: 1, ms: 160 },
    { frame: 0, ms: 160 },
    { frame: 1, ms: 160 },
    { frame: 0, ms: 160 },
    { frame: 1, ms: 160 },
    { frame: 0, ms: 160 },
  ],
};

/** How long the half-way frame shows as he sits down or gets up. */
export const SIT_STEP_MS = 140;

/**
 * Where a frame sits in a mood sheet (public/ui/peon-moods-{happy,tired}-v1.png, 64px cells, 3 by 5):
 * [column, row]. `sat` takes the blink row (0 open, 1 half, 2 closed) and the mouth (0 to 2).
 */
export function moodCell(kind: "yawn" | "rub", frame: number): [number, number];
export function moodCell(kind: "getUp"): [number, number];
export function moodCell(kind: "sat", blink: number, mouth: number): [number, number];
export function moodCell(kind: "yawn" | "rub" | "getUp" | "sat", a = 0, b = 0): [number, number] {
  const clamp = (v: number, max: number) => (Number.isFinite(v) ? Math.max(0, Math.min(max, Math.round(v))) : 0);
  if (kind === "yawn") return [clamp(a, 2), 0];
  if (kind === "rub") return [clamp(a, 1), 1];
  if (kind === "getUp") return [2, 1];
  return [clamp(b, 2), 2 + clamp(a, 2)];
}
